import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import type { Papel } from '../shared/api/tipos.ts';

export interface ItemNavegacao {
  readonly caminho: string;
  readonly rotulo: string;
  readonly icone: ComponentType<SvgIconProps>;
  /** Papéis que veem o item. Ausente vale para todo mundo. */
  readonly papeis?: readonly Papel[];
  /**
   * Fora da lateral, mas ainda com nome.
   *
   * A tela existe e tem rota; o que ela não tem é entrada no menu, porque se
   * chega a ela de dentro de outra. Sem isto o cabeçalho dela mostraria o
   * nome do sistema no lugar do nome da tela — `itemPorCaminho` é quem o
   * responde, e ele continua encontrando o item oculto.
   */
  readonly oculto?: boolean;
}

export const ITENS_NAVEGACAO: readonly ItemNavegacao[] = [
  {
    caminho: '/clientes',
    rotulo: 'Clientes',
    icone: BusinessOutlinedIcon,
  },
  {
    // O contador não entra: esta lista é a carteira do escritório, e nela
    // estão os concorrentes dele.
    caminho: '/contabilidades',
    rotulo: 'Contabilidades',
    icone: AccountBalanceOutlinedIcon,
    papeis: ['administrador', 'usuario'],
  },
  {
    caminho: '/arquivos-fiscais',
    rotulo: 'Arquivos fiscais',
    icone: DescriptionOutlinedIcon,
  },
  {
    // Depois de Arquivos fiscais de propósito: a pergunta "o que falta
    // resolver?" vem de quem acabou de olhar o que chegou.
    caminho: '/pendencias',
    rotulo: 'Pendências',
    icone: RuleOutlinedIcon,
  },
  {
    // Fora da lateral: o que saiu de um mês se olha na aba de histórico de
    // Pendências, ao lado do que ainda falta — eram duas portas para a mesma
    // pergunta, e a de lá chega com o mês e a loja já escolhidos. A tela
    // continua existindo para o log inteiro, sem recorte de mês, e é a aba
    // que leva até ela.
    caminho: '/envios-de-email',
    rotulo: 'Envios de e-mail',
    icone: MarkEmailReadOutlinedIcon,
    oculto: true,
  },
  {
    caminho: '/usuarios',
    rotulo: 'Usuários',
    icone: GroupOutlinedIcon,
    papeis: ['administrador'],
  },
];

/**
 * Itens visíveis para um papel.
 *
 * Esconder o item é só para a tela não oferecer o que não funciona — a rota da
 * API recusa por conta própria. Quem decide permissão é o servidor.
 */
export function itensVisiveis(papel: Papel): readonly ItemNavegacao[] {
  return ITENS_NAVEGACAO.filter(
    (item) =>
      item.oculto !== true && (item.papeis === undefined || item.papeis.includes(papel)),
  );
}

export function itemPorCaminho(caminho: string): ItemNavegacao | undefined {
  return ITENS_NAVEGACAO.find((item) => item.caminho === caminho);
}
